type Range = [number, number];

function clamp(v: number, min: number, max: number) {
    return Math.max(min, Math.min(max, v));
}

function toRanges(tr: TimeRanges, start: number, end: number): Range[] {
    const out: Range[] = [];
    for (let i = 0; i < tr.length; i++) {
        const s = tr.start(i);
        const e = tr.end(i);
        const is = Math.max(s, start);
        const ie = Math.min(e, end);
        if (ie > is) {out.push([is - start, ie - start]);}
    }

    return out;
}

function makeTimeRanges(ranges: Range[]): TimeRanges {
    return {
        length: ranges.length,
        start(i: number) {
            if (i < 0 || i >= ranges.length) {throw new DOMException("IndexSizeError");}

            return ranges[i][0];
        },
        end(i: number) {
            if (i < 0 || i >= ranges.length) {throw new DOMException("IndexSizeError");}

            return ranges[i][1];
        },
    } as TimeRanges;
}

export class VideoClipper extends HTMLElement {
    static get observedAttributes() {
        return ["src", "start", "end"];
    }

    private video: HTMLVideoElement;
    private start = 0;
    private end = 0;

    constructor() {
        super();
        const shadow = this.attachShadow({ mode: "open" });
        this.video = document.createElement("video");
        this.video.playsInline = true;
        this.video.style.width = "100%";
        this.video.style.height = "100%";
        shadow.appendChild(this.video);

        const forwardEvents = [
            "loadedmetadata",
            "durationchange",
            "timeupdate",
            "play",
            "pause",
            "seeking",
            "seeked",
            "progress",
            "volumechange",
            "ratechange",
            "waiting",
            "playing",
            "ended",
            "error",
        ] as const;

        forwardEvents.forEach(type => {
            this.video.addEventListener(type, () => {
                if (type === "timeupdate" || type === "seeking") {
                    if (this.end > 0 && this.video.currentTime > this.end) {
                        this.video.currentTime = this.end;
                        this.video.pause();
                    }
                    if (this.video.currentTime < this.start) {this.video.currentTime = this.start;}
                }
                this.dispatchEvent(new Event(type, { bubbles: true, composed: true }));
            });
        });
    }

    connectedCallback() {
        this.syncFromAttrs();
    }

    attributeChangedCallback() {
        this.syncFromAttrs();
    }

    private syncFromAttrs() {
        const src = this.getAttribute("src") ?? "";
        const s = Number(this.getAttribute("start") ?? "0");
        const e = Number(this.getAttribute("end") ?? "0");
        this.start = Number.isFinite(s) ? s : 0;
        this.end = Number.isFinite(e) ? e : 0;

        if (src && this.video.src !== src) {this.video.src = src;}
    }

    play() { return this.video.play(); }
    pause() { return this.video.pause(); }
    load() { return this.video.load(); }

    get paused() { return this.video.paused; }
    get ended() { return this.video.ended; }

    get volume() { return this.video.volume; }
    set volume(v: number) { this.video.volume = v; }

    get muted() { return this.video.muted; }
    set muted(v: boolean) { this.video.muted = v; }

    get playbackRate() { return this.video.playbackRate; }
    set playbackRate(v: number) { this.video.playbackRate = v; }

    get currentTime() {
        return clamp(this.video.currentTime - this.start, 0, this.duration);
    }
    set currentTime(relSeconds: number) {
        const rel = clamp(relSeconds, 0, this.duration);
        this.video.currentTime = this.start + rel;
    }

    get duration() {
        if (this.end > 0) {return Math.max(0, this.end - this.start);}
        const d = this.video.duration;

        return Number.isFinite(d) ? Math.max(0, d - this.start) : NaN;
    }

    get buffered() {
        if (!(this.end > 0)) {return this.video.buffered;}

        return makeTimeRanges(toRanges(this.video.buffered, this.start, this.end));
    }

    get seekable() {
        if (!(this.end > 0)) {return this.video.seekable;}

        return makeTimeRanges(toRanges(this.video.seekable, this.start, this.end));
    }

    get mediaEl() { return this.video; }
}

customElements.define("video-clipper", VideoClipper);
